import React, { FC, useEffect, useState } from 'react'
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'

// Components
import { ShareMetaSidebar } from '../components/ShareMetaSidebar'
import Footer from 'components/Footer'
import ReportShareModal from 'modules/shares/components/ReportShareModal'
import { Document } from 'modules/shares/components/Document'
import DocumentHeader from 'modules/shares/components/DocumentHeader'
import { EmbeddedDocumentHeader } from 'modules/shares/components/EmbeddedDocumentHeader'
import DocumentSidebarLayout from 'containers/DocumentSidebarLayout'
import { NotEmbedded, IsEmbedded } from 'components/Embedding'
import { ProjectsMenu } from 'modules/projects/components/ProjectsMenu'
import { IsSignedIn } from 'containers/IsSignedIn'
import { CwvRoutes, CWV_ROUTES, isCwvRoute } from '../ComponentsWebView'

// Styles
import { ResponsiveValues } from 'styles/breakpoints'
import {
  ProcessingContainer,
  PageContent,
  ProcessingMessage,
  PageLoading,
  StyledSpinner,
} from './DocumentView.styles'

// Hooks
import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { useHandleSettingsQueryParamIfNeeded } from './DocumentView.hooks'
import {
  useUnfollowShareFromEmail,
  useGetVersionQueryWithPolling,
} from '../operations'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { useShareWorkspaceSidebar } from 'modules/shares/hooks/useShareWorkspaceSidebar'
import { SearchCwvContext } from 'modules/shares/ComponentsWebView/SearchCwvContext'

// Constants
import { RouteParams } from 'constants/routes'

// Utils
import { getVersionRenderStatus } from 'utils/renderStatus'
import { useURLQuerySearch } from 'utils'
import {
  showWorkspaceSettings,
  canUserAdministerShares,
} from 'modules/workspace/utils'

// Icons
import { ReactComponent as DocumentProcessing } from 'icons/document-processing-64.svg'
import { ReactComponent as CloudArrowUp } from 'icons/cloud-arrow-up-64.svg'
import { ReactComponent as WarningTriangle } from 'icons/warning-triangle-64.svg'

/**
 * CONSTANTS
 */
const COLUMNS_WITH_SIDEBAR: ResponsiveValues<number> = [1, 1, 1, 2, 3, 4, 5, 6]
const COLUMNS_WITHOUT_SIDEBAR: ResponsiveValues<number> = [
  2,
  2,
  2,
  3,
  4,
  5,
  6,
  7,
]

/**
 * TYPES
 */
interface DocumentSidebarLayoutHeaderProps {
  isSidebarRightOpen: boolean
  toggleSidebarRight: () => void
  isSidebarLeftOpen: boolean
  setSidebarLeftOpen: React.Dispatch<React.SetStateAction<boolean>>
}

interface DocumentSidebarLayoutChildrenProps {
  isSidebarRightOpen?: boolean
  isMobile?: boolean
}

/**
 * COMPONENT
 */
const DocumentView: FC = () => {
  const { showModal } = useModalContext()

  const history = useHistory()
  const location = useLocation<{ unsubscribe: boolean }>()
  const { shareID } = useParams<
    RouteParams<'SHARE_VIEW'> | RouteParams<'SHARE_PAGE_VIEW'>
  >()
  const { path } = useRouteMatch()
  const [search, setSearchValue, debouncedSearch] = useURLQuerySearch(
    location,
    history
  )

  const [searchCwv, setSearchCwv] = useState('')

  const versioning = useVersioning()
  const { versionShortId, share, latestVersion } = versioning
  const currentVersion = !versioning.loading ? versioning.currentVersion : null
  const { data, error } = useGetVersionQueryWithPolling(shareID, versionShortId)
  const documentName = share.name

  useEffect(() => {
    if (versioning.artboardThumbnail !== '') {
      // clear the artboard thumbnail
      versioning.setArtboardThumbnail('')
    }
  }, [versioning])

  // Handle potential deeplink to settings modal
  useHandleSettingsQueryParamIfNeeded(share, currentVersion, latestVersion)

  const {
    shouldRenderWorkspaceSidebar,
    workspace,
    workspaces,
  } = useShareWorkspaceSidebar(share)

  // Note: this is not the best place to handle the mutation
  // that unfollows a share, but is needed here because is using hooks
  useUnfollowShareFromEmail({
    id: share.identifier,
    userWantsToStopReceivingEmailNotifications: location?.state?.unsubscribe,
    subscriptionStatus: share.subscriptionStatus,
    versionShortId,
  })

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const parsedRenderStatus = getVersionRenderStatus(data?.share?.version)

  return (
    <SearchCwvContext.Provider value={{ searchCwv }}>
      <DocumentSidebarLayout
        title={documentName}
        darkBackground
        header={({
          toggleSidebarRight,
          isSidebarRightOpen,
          isSidebarLeftOpen,
          setSidebarLeftOpen,
        }: DocumentSidebarLayoutHeaderProps) => (
          <>
            <NotEmbedded>
              <DocumentHeader
                toggleSidebarRight={toggleSidebarRight}
                sidebarRightOpen={isSidebarRightOpen}
                search={search}
                setSearchValue={setSearchValue}
                setSearchCwv={setSearchCwv}
                isSidebarLeftOpen={isSidebarLeftOpen}
                setSidebarLeftOpen={setSidebarLeftOpen}
              />
            </NotEmbedded>
            <IsEmbedded>
              <EmbeddedDocumentHeader empty={false} documentName={share.name} />
            </IsEmbedded>
          </>
        )}
        sidebarLeft={
          shouldRenderWorkspaceSidebar ? (
            <ProjectsMenu
              workspace={workspace!}
              workspaces={workspaces!}
              showWorkspaceSettings={showWorkspaceSettings(workspace)}
              canUserAdministerShares={canUserAdministerShares(workspace)}
            />
          ) : undefined
        }
        sidebarRight={
          <NotEmbedded>
            <ShareMetaSidebar />
          </NotEmbedded>
        }
        footer={
          isCwvRoute(path) ? undefined : (
            <Footer
              extraContent={
                !share.workspace.userIsMember && (
                  <IsSignedIn>
                    <li>
                      <span
                        onClick={() =>
                          showModal(ReportShareModal, {
                            shortId: shareID,
                          })
                        }>
                        Report Document
                      </span>
                    </li>
                  </IsSignedIn>
                )
              }
            />
          )
        }>
        {({
          isSidebarRightOpen,
          isMobile,
        }: DocumentSidebarLayoutChildrenProps) => {
          const columns =
            isSidebarRightOpen && !isMobile
              ? COLUMNS_WITH_SIDEBAR
              : COLUMNS_WITHOUT_SIDEBAR

          return (
            <PageContent>
              {
                {
                  loading: (
                    <PageLoading>
                      <StyledSpinner size="40px" primary />
                    </PageLoading>
                  ),
                  uploading: (
                    <ProcessingContainer>
                      <ProcessingMessage
                        icon={<CloudArrowUp />}
                        iconSize="medium"
                        title="Uploading document…"
                        description="Once it’s uploaded your document will be processed and will appear here."
                      />
                    </ProcessingContainer>
                  ),
                  processing: (
                    <ProcessingContainer>
                      <ProcessingMessage
                        icon={<DocumentProcessing />}
                        iconSize="medium"
                        title="Processing document…"
                        description="This document is being processed. When
                              it’s ready, any Prototypes, Artboards and Symbols
                              in the document will appear here."
                      />
                    </ProcessingContainer>
                  ),
                  failed: (
                    <ProcessingContainer>
                      <ProcessingMessage
                        icon={<WarningTriangle />}
                        iconSize="medium"
                        title="We could not process this document"
                        description="We’re sorry, but something went wrong
                              while processing this document. Please try saving
                              the document again."
                      />
                    </ProcessingContainer>
                  ),
                  ready: (
                    <Switch>
                      <CwvRoutes exact path={CWV_ROUTES} />
                      <Route>
                        <Document
                          columns={columns}
                          search={debouncedSearch}
                          showPrototypeArtboards={!debouncedSearch}
                        />
                      </Route>
                    </Switch>
                  ),
                }[parsedRenderStatus]
              }
            </PageContent>
          )
        }}
      </DocumentSidebarLayout>
    </SearchCwvContext.Provider>
  )
}

export default DocumentView
